import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const COURSES = 'courses';

/**
 * Make lesson ids unique across the entire curriculum. Duplicate ids (imported data,
 * copy/paste, or rare uid() collisions) cause students to open the wrong lesson because
 * routing uses find() by id.
 */
function assignUniqueLessonIds(curriculum) {
  const used = new Set();
  return (curriculum || []).map((section, si) => {
    const lessons = section.lessons || [];
    const newLessons = lessons.map((lesson, li) => {
      const positional = `l-${si}-${li}`;
      const raw = lesson.id != null && String(lesson.id).trim() !== '' ? String(lesson.id) : '';
      let candidate = raw && !used.has(raw) ? raw : positional;
      let u = 0;
      while (used.has(candidate)) {
        u += 1;
        candidate = `${positional}-u${u}`;
      }
      used.add(candidate);
      return { ...lesson, id: candidate };
    });
    return { ...section, lessons: newLessons };
  });
}

/** Read price from Firestore; supports price, amount, price_amount, course_price, pricing.amount. */
function getPrice(d) {
  const raw =
    d.price ??
    d.amount ??
    d.price_amount ??
    d.course_price ??
    (d.pricing && (d.pricing.amount ?? d.pricing.price));
  if (raw === undefined || raw === null || raw === '') return 'Free';
  return raw;
}

function docToCard(docSnap) {
  if (!docSnap?.exists?.()) return null;
  const d = docSnap.data();
  const id = docSnap.id;
  const instructor = (d.instructor && typeof d.instructor === 'object' && d.instructor.name) || d.instructor || 'Instructor';
  return {
    id,
    title: d.title || '',
    instructor: typeof instructor === 'string' ? instructor : 'Instructor',
    thumbnail: d.thumbnail || '',
    level: d.level || 'Beginner',
    duration: d.duration || '',
    enrolled: d.enrolled_count ?? 0,
    rating: d.rating ?? 0,
    reviewCount: d.review_count ?? 0,
    price: getPrice(d),
    isPopular: !!d.is_popular,
    isTrending: !!d.is_trending,
    published: !!d.published,
  };
}

function docToDetail(docSnap) {
  if (!docSnap?.exists?.()) return null;
  const d = docSnap.data();
  const id = docSnap.id;
  const inst = d.instructor || {};
  const instructor = typeof inst === 'object' ? { name: inst.name || '', title: inst.title || '', bio: inst.bio || '', avatar: inst.avatar || '' } : { name: String(inst), title: '', bio: '', avatar: '' };
  const curriculumRaw = Array.isArray(d.curriculum) ? d.curriculum.map((s, si) => ({
    id: s.id || 's-' + si,
    title: s.title || 'Section ' + (si + 1),
    lessons: (s.lessons || []).map((l, li) => {
      const videoUrl = l.video_url || l.videoUrl || '';
      let lessonType = l.type;
      if (lessonType !== 'text' && lessonType !== 'video') {
        lessonType = videoUrl ? 'video' : 'text';
      }
      return {
        id: l.id,
        title: l.title || 'Lesson ' + (li + 1),
        duration: l.duration || '0:00',
        type: lessonType,
        video_url: videoUrl,
        content: l.content || '',
        content_format: l.content_format || 'html',
        quiz: l.quiz || null,
        completed: false,
      };
    }),
  })) : [];
  const curriculum = assignUniqueLessonIds(curriculumRaw);
  return {
    id,
    title: d.title || '',
    slug: d.slug || id,
    description: d.description || '',
    instructor,
    thumbnail: d.thumbnail || '',
    banner: d.banner || '',
    level: d.level || 'Beginner',
    duration: d.duration || '',
    lessons_count: curriculum.reduce((acc, s) => acc + (s.lessons?.length || 0), 0),
    enrolled_count: d.enrolled_count ?? 0,
    rating: d.rating ?? 0,
    review_count: d.review_count ?? 0,
    reviewCount: d.review_count ?? 0,
    what_you_learn: d.what_you_learn || [],
    whatYouLearn: d.what_you_learn || [],
    requirements: d.requirements || [],
    curriculum,
    progress: 0,
    enrolled: false,
    price: getPrice(d),
  };
}

export async function listCourses() {
  const snap = await getDocs(collection(db, COURSES));
  const list = [];
  snap.forEach((docSnap) => {
    const card = docToCard(docSnap);
    if (card && card.published) list.push(card);
  });
  return list;
}

export async function getCourse(id) {
  const docSnap = await getDoc(doc(db, COURSES, id));
  return docToDetail(docSnap);
}

export async function listCoursesForHome() {
  const q = query(collection(db, COURSES), where('published', '==', true));
  const snap = await getDocs(q);
  const list = [];
  snap.forEach((docSnap) => {
    const card = docToCard(docSnap);
    if (card) list.push(card);
  });
  return list;
}

export async function listRelatedCourses(excludeId, limit) {
  const all = await listCoursesForHome();
  return all.filter((c) => c.id !== excludeId).slice(0, limit || 4);
}
